// src/components/leitner/TaskPerformanceView.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { 
  BarChart3, Users, Target, Clock, TrendingUp, TrendingDown, ChevronDown, ChevronUp,
  Trophy, AlertCircle, CheckCircle, XCircle, ArrowLeft, Calendar,
  User, Award, Zap, BookOpen, RefreshCw, Package, Eye, Flame, Activity
} from 'lucide-react';
import { useLearnerAnalytics, useTaskAnalytics } from '../../hooks/useLearnerAnalytics';
import { UserService } from '../../services/userService';
import { useAuth } from '../../contexts/AuthContext';
import { MathRenderer } from '../math/MathRenderer';

const TaskPerformanceView = ({ 
  bookId, 
  task, 
  questions, 
  styling, 
  onBack,
  viewMode = 'owner'
}) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(viewMode === 'learner' ? 'my-progress' : 'overview');
  const [expandedQuestion, setExpandedQuestion] = useState(null);
  const [expandedLearner, setExpandedLearner] = useState(null);
  const [userProfiles, setUserProfiles] = useState({});

  // Use analytics hooks
  const { 
    taskAnalytics, 
    questionAnalytics, 
    loading: taskLoading, 
    refresh: refreshTaskAnalytics 
  } = useTaskAnalytics(bookId, task.id);

  const { 
    analytics: learnerAnalytics, 
    insights, 
    loading: learnerLoading,
    loadAnalytics: refreshLearnerAnalytics
  } = useLearnerAnalytics(bookId, task.id, user?.uid);

  const loading = viewMode === 'owner' ? taskLoading : learnerLoading;

  // Load user profiles for learners list
  useEffect(() => {
    if (taskAnalytics?.learners?.length > 0) {
      loadUserProfiles(taskAnalytics.learners.map(l => l.userId).filter(Boolean));
    }
  }, [taskAnalytics?.learners]);

  const loadUserProfiles = async (userIds) => {
    const profiles = {};
    await Promise.all(userIds.map(async (uid) => {
      try {
        profiles[uid] = await UserService.getUserProfile(uid);
      } catch { profiles[uid] = null; }
    }));
    setUserProfiles(prev => ({ ...prev, ...profiles }));
  };

  const handleRefresh = () => {
    if (viewMode === 'owner') {
      refreshTaskAnalytics();
    }
    refreshLearnerAnalytics();
  };

  // Helper to find question by ID (including subQuestions)
  const getQuestionById = (qId) => {
    const direct = questions?.find(q => q.id === qId);
    if (direct) return direct;
    
    for (const q of (questions || [])) {
      if (q.subQuestions) {
        const sub = q.subQuestions.find(sq => sq.id === qId);
        if (sub) {
          return { ...sub, parentStemText: q.stemText, parentStemImage: q.stemImage };
        }
      }
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin h-8 w-8 border-2 border-t-transparent rounded-full"
             style={{ borderColor: styling?.primaryColor || '#6366f1', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  const tabs = viewMode === 'owner' 
    ? [
        { id: 'overview', label: 'Overview', icon: BarChart3 },
        { id: 'learners', label: 'Learners', icon: Users, count: taskAnalytics?.totalLearners || 0 },
        { id: 'questions', label: 'Questions', icon: BookOpen },
        { id: 'my-progress', label: 'My Progress', icon: User }
      ]
    : [
        { id: 'my-progress', label: 'My Progress', icon: User },
        { id: 'trends', label: 'Trends', icon: Activity },
        { id: 'questions', label: 'Question Analysis', icon: BookOpen }
      ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-slate-300" />
          </button>
          <div>
            <h2 className="text-2xl font-bold text-white">{task.name}</h2>
            <p className="text-slate-400">Performance Analytics</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={handleRefresh}
            className="p-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 text-slate-300 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
              activeTab === tab.id
                ? 'text-white'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/50'
            }`}
            style={activeTab === tab.id ? { backgroundColor: styling?.primaryColor || '#6366f1' } : {}}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
            {tab.count !== undefined && (
              <span className="ml-1 px-1.5 py-0.5 bg-white/20 rounded text-xs">{tab.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && viewMode === 'owner' && (
        <OverviewTab 
          taskAnalytics={taskAnalytics}
          styling={styling}
        />
      )}

      {activeTab === 'learners' && viewMode === 'owner' && (
        <LearnersTab 
          learners={taskAnalytics?.learners || []}
          userProfiles={userProfiles}
          expandedLearner={expandedLearner}
          setExpandedLearner={setExpandedLearner}
          styling={styling}
        />
      )}

      {activeTab === 'questions' && (
        <QuestionsTab 
          questions={questions}
          questionAnalytics={questionAnalytics}
          expandedQuestion={expandedQuestion}
          setExpandedQuestion={setExpandedQuestion}
          getQuestionById={getQuestionById}
          viewMode={viewMode}
        />
      )}
      {activeTab === 'my-progress' && (
        <MyProgressTab 
          learnerAnalytics={learnerAnalytics}
          insights={insights}
          styling={styling}
          getQuestionById={getQuestionById}
        />
      )}

      {activeTab === 'trends' && viewMode === 'learner' && (
        <TrendsTab 
          learnerAnalytics={learnerAnalytics}
          styling={styling}
        />
      )}
    </div>
  );
};

// ==================== OVERVIEW TAB (Owner) ====================
const OverviewTab = ({ taskAnalytics, styling }) => {
  if (!taskAnalytics) {
    return (
      <div className="text-center py-16 bg-slate-800 rounded-xl">
        <Users className="h-16 w-16 text-slate-600 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-slate-300 mb-2">No Data Yet</h3>
        <p className="text-slate-500">Analytics will appear once learners start studying</p>
      </div>
    );
  }

  const { totalLearners, activeLearners, avgMastery, avgAccuracy, totalSessions, avgStreak, learnerBreakdown } = taskAnalytics;

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard icon={Users} label="Total Learners" value={totalLearners} color="blue" />
        <StatCard icon={Activity} label="Active (7d)" value={activeLearners} color="green" />
        <StatCard icon={Trophy} label="Avg Mastery" value={`${avgMastery}%`} color="amber" />
        <StatCard icon={Target} label="Avg Accuracy" value={`${avgAccuracy}%`} color="purple" />
        <StatCard icon={BookOpen} label="Total Sessions" value={totalSessions} color="indigo" />
        <StatCard icon={Flame} label="Avg Streak" value={avgStreak} color="orange" />
      </div>

      {/* Learner Breakdown */}
      <div className="bg-slate-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Package className="h-5 w-5 text-indigo-400" />
          Learner Progress Breakdown
        </h3>
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: 'Struggling', count: learnerBreakdown.struggling || 0, color: 'bg-red-500', desc: '<25% mastery' },
            { label: 'Learning', count: learnerBreakdown.learning || 0, color: 'bg-amber-500', desc: '25-50% mastery' },
            { label: 'Proficient', count: learnerBreakdown.proficient || 0, color: 'bg-blue-500', desc: '50-80% mastery' },
            { label: 'Mastered', count: learnerBreakdown.mastered || 0, color: 'bg-green-500', desc: '>80% mastery' }
          ].map(({ label, count, color, desc }) => (
            <div key={label} className="bg-slate-700 rounded-lg p-4 text-center">
              <div className={`w-4 h-4 rounded-full ${color} mx-auto mb-2`} />
              <div className="text-2xl font-bold text-white">{count}</div>
              <div className="text-sm text-slate-300">{label}</div>
              <div className="text-xs text-slate-500">{desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Activity Chart Placeholder */}
      <div className="bg-slate-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Activity className="h-5 w-5 text-green-400" />
          Engagement Overview
        </h3>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <p className="text-slate-400 text-sm mb-2">Active vs Inactive</p>
            <div className="h-4 bg-slate-700 rounded-full overflow-hidden flex">
              <div 
                className="h-full bg-green-500"
                style={{ width: `${totalLearners > 0 ? (activeLearners / totalLearners) * 100 : 0}%` }}
              />
              <div 
                className="h-full bg-slate-600"
                style={{ width: `${totalLearners > 0 ? ((totalLearners - activeLearners) / totalLearners) * 100 : 100}%` }}
              />
            </div>
            <div className="flex justify-between text-xs text-slate-500 mt-1">
              <span>{activeLearners} active</span>
              <span>{totalLearners - activeLearners} inactive</span>
            </div>
          </div>
          <div>
            <p className="text-slate-400 text-sm mb-2">Average Performance</p>
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <div className="h-4 bg-slate-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-amber-500 to-green-500"
                    style={{ width: `${avgMastery}%` }}
                  />
                </div>
              </div>
              <span className="text-2xl font-bold text-white">{avgMastery}%</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== LEARNERS TAB (Owner) ====================
const LearnersTab = ({ learners, userProfiles, expandedLearner, setExpandedLearner, styling }) => {
  const sortedLearners = [...(learners || [])].sort((a, b) => (b.masteryPercent || 0) - (a.masteryPercent || 0));

  if (sortedLearners.length === 0) {
    return (
      <div className="text-center py-16 bg-slate-800 rounded-xl">
        <Users className="h-16 w-16 text-slate-600 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-slate-300 mb-2">No Learners Yet</h3>
        <p className="text-slate-500">Learners will appear here once they start studying</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="bg-slate-800 rounded-xl p-4">
        <p className="text-slate-400 text-sm">
          {sortedLearners.length} learners • Sorted by mastery (highest first)
        </p>
      </div>

      {/* Learners List */}
      <div className="space-y-2">
        {sortedLearners.map((learner, index) => {
          const profile = userProfiles[learner.userId];
          const isExpanded = expandedLearner === learner.userId;
          
          return (
            <div key={learner.userId} className="bg-slate-800 rounded-lg overflow-hidden">
              <button
                onClick={() => setExpandedLearner(isExpanded ? null : learner.userId)}
                className="w-full p-4 flex items-center justify-between hover:bg-slate-700/70 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    index === 0 ? 'bg-amber-500 text-amber-900' :
                    index === 1 ? 'bg-slate-400 text-slate-800' :
                    index === 2 ? 'bg-amber-700 text-amber-200' :
                    'bg-slate-600 text-slate-300'
                  }`}>
                    {index + 1}
                  </span>
                  <div className="w-10 h-10 rounded-full bg-slate-600 flex items-center justify-center">
                    {profile?.photoURL ? (
                      <img src={profile.photoURL} alt="" className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <User className="h-5 w-5 text-slate-400" />
                    )}
                  </div>
                  <div className="text-left">
                    <p className="text-slate-200 font-medium">
                      {profile?.displayName || profile?.email || 'Anonymous'}
                    </p>
                    <p className="text-xs text-slate-400">
                      {learner.totalSessions || 0} sessions • Streak: {learner.currentStreak || 0}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className={`font-bold ${
                      learner.masteryPercent >= 70 ? 'text-green-400' :
                      learner.masteryPercent >= 40 ? 'text-amber-400' : 'text-red-400'
                    }`}>
                      {learner.masteryPercent || 0}% mastery
                    </p>
                    <p className="text-xs text-slate-400">
                      {learner.totalCorrect || 0}/{learner.totalAttempts || 0} = {learner.accuracy || 0}%
                    </p>
                  </div>
                  {isExpanded ? (
                    <ChevronUp className="h-5 w-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-slate-400" />
                  )}
                </div>
              </button>
              
              {isExpanded && (
                <div className="px-4 pb-4 border-t border-slate-600">
                  <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mt-4">
                    <div className="text-center p-3 bg-slate-800 rounded-lg">
                      <p className="text-2xl font-bold text-white">{learner.total || 0}</p>
                      <p className="text-xs text-slate-400">Questions</p>
                    </div>
                    <div className="text-center p-3 bg-slate-800 rounded-lg">
                      <p className="text-2xl font-bold text-purple-400">{learner.totalAttempts || 0}</p>
                      <p className="text-xs text-slate-400">Attempts</p>
                    </div>
                    <div className="text-center p-3 bg-slate-800 rounded-lg">
                      <p className="text-2xl font-bold text-blue-400">{learner.totalCorrect || 0}</p>
                      <p className="text-xs text-slate-400">Correct</p>
                    </div>
                    <div className="text-center p-3 bg-slate-800 rounded-lg">
                      <p className="text-2xl font-bold text-green-400">{learner.mastered || 0}</p>
                      <p className="text-xs text-slate-400">Mastered</p>
                    </div>
                    <div className="text-center p-3 bg-slate-800 rounded-lg">
                      <p className="text-2xl font-bold text-amber-400">{learner.learning || 0}</p>
                      <p className="text-xs text-slate-400">Learning</p>
                    </div>
                    <div className="text-center p-3 bg-slate-800 rounded-lg">
                      <p className="text-2xl font-bold text-red-400">{learner.struggling || 0}</p>
                      <p className="text-xs text-slate-400">Struggling</p>
                    </div>
                  </div>
                  
                  {/* Box Distribution */}
                  <div className="mt-4">
                    <p className="text-sm text-slate-400 mb-2">Leitner Box Distribution</p>
                    <div className="flex gap-2">
                      {(learner.boxDistribution || [0,0,0,0,0]).map((count, box) => (
                        <div key={box} className="flex-1 text-center">
                          <div 
                            className={`h-16 rounded-lg flex items-end justify-center pb-1 ${
                              box === 0 ? 'bg-red-500/20' :
                              box === 1 ? 'bg-orange-500/20' :
                              box === 2 ? 'bg-amber-500/20' :
                              box === 3 ? 'bg-blue-500/20' :
                              'bg-green-500/20'
                            }`}
                          >
                            <span className={`text-lg font-bold ${
                              box === 0 ? 'text-red-400' :
                              box === 1 ? 'text-orange-400' :
                              box === 2 ? 'text-amber-400' :
                              box === 3 ? 'text-blue-400' :
                              'text-green-400'
                            }`}>{count}</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-1">Box {box + 1}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ==================== QUESTIONS TAB ====================
const QuestionsTab = ({ questions, questionAnalytics, expandedQuestion, setExpandedQuestion, getQuestionById, viewMode }) => {
  
  // Build list of all valid question IDs from the task (including subQuestions)
  const allQuestionIds = useMemo(() => {
    const ids = new Set();
    (questions || []).forEach(q => {
      if (q.type === 'stem' && q.subQuestions) {
        q.subQuestions.forEach(sq => ids.add(sq.id || `${q.id}_sub_${q.subQuestions.indexOf(sq)}`));
      } else {
        ids.add(q.id);
      }
    });
    return ids;
  }, [questions]);

  // Merge analytics with all questions - filter analytics to only valid IDs + show all questions
  const sortedQuestions = useMemo(() => {
    const questionsMap = new Map();
    
    // First add all questions from the task (even without analytics)
    allQuestionIds.forEach(qId => {
      const question = getQuestionById(qId);
      questionsMap.set(qId, {
        id: qId,
        question,
        accuracy: null, // No data yet
        stats: null,
        hasData: false
      });
    });
    
    // Then overlay analytics data for questions that have it (only if ID exists in task)
    Object.entries(questionAnalytics || {}).forEach(([qId, stats]) => {
      if (allQuestionIds.has(qId)) {
        const question = getQuestionById(qId);
        questionsMap.set(qId, {
          id: qId,
          question,
          accuracy: stats.accuracy || 0,
          stats,
          hasData: true
        });
      }
    });
    
    // Convert to array and sort: questions with data first (by accuracy), then questions without data
    return Array.from(questionsMap.values())
      .sort((a, b) => {
        if (a.hasData && !b.hasData) return -1;
        if (!a.hasData && b.hasData) return 1;
        if (a.hasData && b.hasData) return (a.accuracy || 0) - (b.accuracy || 0);
        return 0;
      });
  }, [questionAnalytics, getQuestionById, allQuestionIds]);

  const questionsWithData = sortedQuestions.filter(q => q.hasData);
  const questionsWithoutData = sortedQuestions.filter(q => !q.hasData);

  if (sortedQuestions.length === 0) {
    return (
      <div className="text-center py-16 bg-slate-800 rounded-xl">
        <BookOpen className="h-16 w-16 text-slate-600 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-slate-300 mb-2">No Questions Found</h3>
        <p className="text-slate-500">This task has no questions configured</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-slate-400 text-sm">
          {sortedQuestions.length} questions • {questionsWithData.length} with analytics
        </p>
        {questionsWithoutData.length > 0 && (
          <span className="text-xs px-2 py-1 bg-slate-700 text-slate-400 rounded-full">
            {questionsWithoutData.length} awaiting data
          </span>
        )}
      </div>

      <div className="space-y-3">
        {sortedQuestions.map((item, index) => {
          const { id: qId, question, accuracy, stats, hasData } = item;
          const isExpanded = expandedQuestion === qId;
          const questionText = question?.text || question?.questionText || question?.parentStemText || `Question ${qId.slice(0, 8)}...`;
          
          return (
            <div key={qId} className="bg-slate-800 rounded-lg overflow-hidden">
              <button
                onClick={() => setExpandedQuestion(isExpanded ? null : qId)}
                className="w-full p-4 flex items-center justify-between hover:bg-slate-700/70 transition-colors"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    !hasData ? 'bg-slate-600 text-slate-400' :
                    accuracy >= 70 ? 'bg-green-500/20 text-green-400' :
                    accuracy >= 40 ? 'bg-amber-500/20 text-amber-400' :
                    'bg-red-500/20 text-red-400'
                  }`}>
                    {index + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <MathRenderer content={questionText} className="text-slate-300 text-sm line-clamp-1" enableMarkdown={true} enableMath={true}/>
                  </div>
                </div>
                <div className="flex items-center gap-4 flex-shrink-0">
                  {hasData ? (
                    <div className="flex items-center gap-2">
                      {accuracy >= 70 ? <CheckCircle className="h-4 w-4 text-green-400" /> :
                       accuracy >= 40 ? <AlertCircle className="h-4 w-4 text-amber-400" /> :
                       <XCircle className="h-4 w-4 text-red-400" />}
                      <span className={`font-medium ${
                        accuracy >= 70 ? 'text-green-400' : 
                        accuracy >= 40 ? 'text-amber-400' : 'text-red-400'
                      }`}>
                        {accuracy}%
                      </span>
                    </div>
                  ) : (
                    <span className="text-xs text-slate-500 italic">No data yet</span>
                  )}
                  {isExpanded ? <ChevronUp className="h-5 w-5 text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-400" />}
                </div>
              </button>
              
              {isExpanded && (
                <div className="px-4 pb-4 pt-2 border-t border-slate-600">
                  {hasData ? (
                    <>
                      <div className="grid grid-cols-4 gap-4 text-center">
                        <div>
                          <p className="text-2xl font-bold text-blue-400">{stats.learnerCount || 0}</p>
                          <p className="text-xs text-slate-400">Learners</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-white">{stats.totalAttempts || 0}</p>
                          <p className="text-xs text-slate-400">Attempts</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-green-400">{stats.totalCorrect || 0}</p>
                          <p className="text-xs text-slate-400">Correct</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-slate-300">{stats.avgBox || '1.0'}</p>
                          <p className="text-xs text-slate-400">Avg Box</p>
                        </div>
                      </div>
                      
                      {/* Difficulty Badge */}
                      <div className="mt-3 flex justify-center">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          stats.difficulty === 'hard' ? 'bg-red-500/20 text-red-400' :
                          stats.difficulty === 'medium' ? 'bg-amber-500/20 text-amber-400' :
                          'bg-green-500/20 text-green-400'
                        }`}>
                          {stats.difficulty === 'hard' ? '🔥 Hard' : stats.difficulty === 'medium' ? '⚡ Medium' : '✨ Easy'}
                        </span>
                      </div>
                      
                      {/* Accuracy Bar */}
                      <div className="mt-3 h-2 bg-slate-600 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${
                            accuracy >= 70 ? 'bg-green-500' : accuracy >= 40 ? 'bg-amber-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${accuracy}%` }}
                        />
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-4">
                      <Clock className="h-8 w-8 text-slate-600 mx-auto mb-2" />
                      <p className="text-slate-400 text-sm">No learner data yet</p>
                      <p className="text-slate-500 text-xs mt-1">Analytics will appear when learners study this question</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ==================== MY PROGRESS TAB ====================
const MyProgressTab = ({ learnerAnalytics, insights, styling, getQuestionById }) => {
  if (!learnerAnalytics || !learnerAnalytics.questionStats) {
    return (
      <div className="text-center py-16 bg-slate-800 rounded-xl">
        <Target className="h-16 w-16 text-slate-600 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-slate-300 mb-2">No Progress Yet</h3>
        <p className="text-slate-500">Start studying to see your performance analytics</p>
      </div>
    );
  }

  const { progress, questionStats, recentSessions, trend } = learnerAnalytics;

  return (
    <div className="space-y-6">
      {/* Insights */}
      {insights.length > 0 && (
        <div className="space-y-2">
          {insights.map((insight, i) => (
            <div 
              key={i}
              className={`p-4 rounded-lg flex items-center gap-3 ${
                insight.type === 'positive' ? 'bg-green-500/10 border border-green-500/20' :
                insight.type === 'warning' ? 'bg-amber-500/10 border border-amber-500/20' :
                'bg-blue-500/10 border border-blue-500/20'
              }`}
            >
              {insight.type === 'positive' ? <TrendingUp className="h-5 w-5 text-green-400 flex-shrink-0" /> :
               insight.type === 'warning' ? <TrendingDown className="h-5 w-5 text-amber-400 flex-shrink-0" /> :
               <Zap className="h-5 w-5 text-blue-400 flex-shrink-0" />}
              <p className={`text-sm ${
                insight.type === 'positive' ? 'text-green-300' :
                insight.type === 'warning' ? 'text-amber-300' :
                'text-blue-300'
              }`}>
                {insight.message}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={BookOpen} label="Questions" value={questionStats.total} color="blue" />
        <StatCard icon={Target} label="Accuracy" value={`${questionStats.accuracy}%`} color="green" />
        <StatCard icon={Trophy} label="Mastery" value={`${questionStats.masteryPercent}%`} color="amber" />
        <StatCard icon={Flame} label="Streak" value={progress.currentStreak || 0} color="orange" />
      </div>

      {/* Mastery Progress */}
      <div className="bg-slate-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Award className="h-5 w-5 text-amber-400" />
          Mastery Progress
        </h3>
        <div className="flex items-center gap-4 mb-4">
          <div className="flex-1">
            <div className="h-4 bg-slate-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-amber-500 to-green-500 transition-all"
                style={{ width: `${questionStats.masteryPercent}%` }}
              />
            </div>
          </div>
          <span className="text-2xl font-bold text-white">{questionStats.masteryPercent}%</span>
        </div>
        <p className="text-slate-400 text-sm">
          {questionStats.mastered} of {questionStats.total} questions mastered (Box 4+)
        </p>
      </div>

      {/* Box Distribution */}
      <div className="bg-slate-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Package className="h-5 w-5 text-indigo-400" />
          Leitner Box Distribution
        </h3>
        <div className="grid grid-cols-5 gap-3">
          {(questionStats.boxDistribution || [0,0,0,0,0]).map((count, index) => (
            <div 
              key={index}
              className={`text-center p-4 rounded-lg ${
                index === 0 ? 'bg-red-500/20' :
                index === 1 ? 'bg-orange-500/20' :
                index === 2 ? 'bg-amber-500/20' :
                index === 3 ? 'bg-blue-500/20' :
                'bg-green-500/20'
              }`}
            >
              <div className={`text-2xl font-bold ${
                index === 0 ? 'text-red-400' :
                index === 1 ? 'text-orange-400' :
                index === 2 ? 'text-amber-400' :
                index === 3 ? 'text-blue-400' :
                'text-green-400'
              }`}>
                {count}
              </div>
              <div className="text-xs text-slate-400">Box {index + 1}</div>
            </div>
          ))}
        </div>
        <p className="text-slate-500 text-xs mt-3 text-center">
          Average Box: {questionStats.avgBox}
        </p>
      </div>

      {/* Study Stats */}
      <div className="bg-slate-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Clock className="h-5 w-5 text-blue-400" />
          Study Stats
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-slate-700/50 rounded-lg p-4 text-center">
            <p className="text-3xl font-bold text-white">{progress.totalSessions || 0}</p>
            <p className="text-sm text-slate-400">Sessions</p>
          </div>
          <div className="bg-slate-700/50 rounded-lg p-4 text-center">
            <p className="text-3xl font-bold text-green-400">{questionStats.totalCorrect}</p>
            <p className="text-sm text-slate-400">Correct</p>
          </div>
          <div className="bg-slate-700/50 rounded-lg p-4 text-center">
            <p className="text-3xl font-bold text-red-400">{questionStats.totalAttempts - questionStats.totalCorrect}</p>
            <p className="text-sm text-slate-400">Incorrect</p>
          </div>
          <div className="bg-slate-700/50 rounded-lg p-4 text-center">
            <p className="text-3xl font-bold text-amber-400">{progress.longestStreak || 0}</p>
            <p className="text-sm text-slate-400">Best Streak</p>
          </div>
        </div>
      </div>

      {/* Recent Sessions */}
      {recentSessions && recentSessions.length > 0 && (
        <div className="bg-slate-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Recent Sessions</h3>
          <div className="space-y-2">
            {recentSessions.slice(0, 5).map((session, index) => (
              <div key={session.id || index} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <span className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                    session.accuracy >= 70 ? 'bg-green-500/20 text-green-400' :
                    session.accuracy >= 40 ? 'bg-amber-500/20 text-amber-400' :
                    'bg-red-500/20 text-red-400'
                  }`}>
                    {session.accuracy}%
                  </span>
                  <div>
                    <p className="text-slate-200 font-medium">
                      {session.correct}/{session.questionsAnswered} correct
                    </p>
                    <p className="text-xs text-slate-400">
                      {session.completedAt?.toDate?.().toLocaleDateString() || '-'} • 
                      {Math.round((session.duration || 0) / 60)}min
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-1 text-xs">
                    {session.boxChanges?.improved > 0 && (
                      <span className="text-green-400">↑{session.boxChanges.improved}</span>
                    )}
                    {session.boxChanges?.declined > 0 && (
                      <span className="text-red-400">↓{session.boxChanges.declined}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== TRENDS TAB ====================
const TrendsTab = ({ learnerAnalytics, styling }) => {
  // Check if we have actual data with attempts, not just empty date scaffolding
  const dailyStats = learnerAnalytics?.dailyStats || [];
  const trend = learnerAnalytics?.trend || {};
  const activeDays = dailyStats.filter(d => d.attempts > 0);
  
  // Show empty state if no daily stats OR no active days with actual attempts
  if (dailyStats.length === 0 || (activeDays.length === 0 && (trend?.totalAttempts || 0) === 0)) {
    return (
      <div className="text-center py-16 bg-slate-800 rounded-xl">
        <Activity className="h-16 w-16 text-slate-600 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-slate-300 mb-2">No Trend Data Yet</h3>
        <p className="text-slate-500">Study for a few days to see your trends</p>
      </div>
    );
  }

  const maxAttempts = Math.max(...dailyStats.map(d => d.attempts), 1);

  return (
    <div className="space-y-6">
      {/* Trend Summary */}
      <div className="bg-slate-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          {trend?.isImproving ? (
            <TrendingUp className="h-5 w-5 text-green-400" />
          ) : trend?.totalAttempts > 0 ? (
            <TrendingDown className="h-5 w-5 text-red-400" />
          ) : (
            <Activity className="h-5 w-5 text-slate-400" />
          )}
          Performance Trend
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <p className={`text-3xl font-bold ${
              trend?.trendDirection > 0 ? 'text-green-400' : 
              trend?.trendDirection < 0 ? 'text-red-400' : 'text-slate-400'
            }`}>
              {trend?.trendDirection > 0 ? '+' : ''}{Math.round(trend?.trendDirection || 0)}%
            </p>
            <p className="text-sm text-slate-400">Accuracy Change</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-white">{trend?.activeDays || activeDays.length || 0}</p>
            <p className="text-sm text-slate-400">Active Days</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-purple-400">{trend?.totalAttempts || 0}</p>
            <p className="text-sm text-slate-400">Total Attempts</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-blue-400">{trend?.overallAccuracy || 0}%</p>
            <p className="text-sm text-slate-400">Overall Accuracy</p>
          </div>
        </div>
      </div>

      {/* Activity Chart */}
      <div className="bg-slate-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Daily Activity</h3>
        {activeDays.length > 0 ? (
          <>
            <div className="flex items-end gap-1 h-32 overflow-x-auto">
              {dailyStats.slice(-30).map((day, i) => (
                <div 
                  key={day.date || i}
                  className="flex-1 min-w-[8px] flex flex-col items-center"
                  title={`${day.date}: ${day.attempts} attempts, ${day.accuracy}% accuracy`}
                >
                  <div 
                    className={`w-full rounded-t transition-all ${
                      day.attempts === 0 ? 'bg-slate-700' :
                      day.accuracy >= 70 ? 'bg-green-500' :
                      day.accuracy >= 40 ? 'bg-amber-500' : 'bg-red-500'
                    }`}
                    style={{ 
                      height: `${day.attempts > 0 ? Math.max((day.attempts / maxAttempts) * 100, 10) : 5}%`
                    }}
                  />
                </div>
              ))}
            </div>
            <div className="flex justify-between text-xs text-slate-500 mt-2">
              <span>{dailyStats[0]?.date}</span>
              <span>{dailyStats[dailyStats.length - 1]?.date}</span>
            </div>
          </>
        ) : (
          <div className="h-32 flex items-center justify-center text-slate-500">
            <p>No activity recorded yet</p>
          </div>
        )}
      </div>

      {/* Box Progress Over Time */}
      <div className="bg-slate-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Box Movement</h3>
        {activeDays.length > 0 ? (
          <div className="space-y-3">
            {activeDays.slice(-7).reverse().map(day => (
              <div key={day.date} className="flex items-center gap-3">
                <span className="text-xs text-slate-500 w-20">{day.date?.slice(5) || '-'}</span>
                <div className="flex-1 flex items-center gap-2">
                  {day.boxImprovements > 0 && (
                    <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded">
                      ↑ {day.boxImprovements}
                    </span>
                  )}
                  {day.boxDeclines > 0 && (
                    <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded">
                      ↓ {day.boxDeclines}
                    </span>
                  )}
                  {(day.boxImprovements || 0) === 0 && (day.boxDeclines || 0) === 0 && (
                    <span className="px-2 py-1 bg-slate-600 text-slate-400 text-xs rounded">
                      — no change
                    </span>
                  )}
                </div>
                <span className="text-sm text-slate-300">{day.accuracy || 0}%</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-slate-500">
            <p>No box movements recorded yet</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== HELPER COMPONENTS ====================
const StatCard = ({ icon: Icon, label, value, subtitle, color }) => {
  const colors = {
    blue: 'bg-blue-500/20 text-blue-400',
    purple: 'bg-purple-500/20 text-purple-400',
    green: 'bg-green-500/20 text-green-400',
    amber: 'bg-amber-500/20 text-amber-400',
    red: 'bg-red-500/20 text-red-400',
    indigo: 'bg-indigo-500/20 text-indigo-400',
    orange: 'bg-orange-500/20 text-orange-400'
  };
  
  return (
    <div className="bg-slate-800 rounded-xl p-4">
      <div className={`w-10 h-10 rounded-lg ${colors[color]} flex items-center justify-center mb-3`}>
        <Icon className="h-5 w-5" />
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
      {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
      <p className="text-sm text-slate-400">{label}</p>
    </div>
  );
};

export default TaskPerformanceView;